/**
 * Lista de sesiones de caja (antes en /cashbox/history).
 *
 * La columna final es la diferencia del arqueo (contado menos efectivo
 * esperado), la misma cifra que registra el cierre. Antes era el saldo de todos
 * los medios, que no coincidía con nada de lo que se cuenta.
 */

import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { History, ChevronRight, Check, X } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDateTime } from '@/lib/utils'
import { t } from '@/i18n'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Pagination } from '@/components/ui/pagination'
import { useUrlPage } from '@/hooks/useUrlState'
import { Badge } from '@/components/ui/badge'

interface CashboxSession {
  id: number
  name: string
  is_open: boolean
  initial_amount: number
  final_amount: number | null
  total_income: number
  total_expense: number
  expected_amount: number
  difference: number
  opening_time: string | null
  closing_time: string | null
  opened_by_name: string | null
  closed_by_name: string | null
  total_sales: number
  total_deposits: number
}

function DifferenceAmount({ session, className = '' }: { session: CashboxSession; className?: string }) {
  if (session.is_open || session.final_amount === null) {
    return <span className={`text-muted-foreground ${className}`}>—</span>
  }
  const tone = session.difference < 0 ? 'text-destructive' : session.difference > 0 ? 'text-success' : ''
  return <span className={`tabular-nums ${tone} ${className}`}>{formatCurrency(session.difference)}</span>
}

export function CashboxSessionsList() {
  const navigate = useNavigate()
  const [page, setPage] = useUrlPage()

  const { data, isLoading } = useQuery({
    queryKey: ['cashbox-history', page],
    queryFn: async () => {
      const response = await api.get(`/cashboxes/history?page=${page}&page_size=20`)
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const sessions = data?.items || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / 20)

  return (
    <div className="space-y-3">
    {/* Content */}
    {isLoading ? (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    ) : sessions.length === 0 ? (
      <div className="flex flex-col items-center justify-center py-12">
        <History className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium">{t('cashbox.noHistory')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('cashbox.noHistoryDesc')}
        </p>
      </div>
    ) : (
      <>
        {/* Mobile: Cards */}
        <div className="space-y-3 lg:hidden">
          {sessions.map((session: CashboxSession) => (
            <Link
              key={session.id}
              to={`/cashbox/history/${session.id}`}
              className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
            <Card
              className="active:bg-muted/50 cursor-pointer touch-manipulation"
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant={session.is_open ? 'warning' : 'success'}>
                    {session.is_open ? t('cashbox.statusOpen') : t('cashbox.statusClosed')}
                  </Badge>
                  <span className="text-xs text-muted-foreground">#{session.id}</span>
                </div>

                {/* Apertura */}
                <div className="flex justify-between items-center py-2 border-b">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('cashbox.opening')}</p>
                    <p className="text-sm font-medium">{session.opened_by_name || '-'}</p>
                    <p className="text-xs text-muted-foreground">
                      {session.opening_time ? formatDateTime(session.opening_time) : '-'}
                    </p>
                  </div>
                  <p className="font-bold text-green-600">
                    {formatCurrency(session.initial_amount)}
                  </p>
                </div>

                {/* Cierre */}
                <div className="flex justify-between items-center py-2 border-b">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('cashbox.closing')}</p>
                    <p className="text-sm font-medium">{session.closed_by_name || '-'}</p>
                    <p className="text-xs text-muted-foreground">
                      {session.closing_time ? formatDateTime(session.closing_time) : '-'}
                    </p>
                  </div>
                  <p className={`font-bold ${session.final_amount !== null ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {session.final_amount !== null ? formatCurrency(session.final_amount) : '-'}
                  </p>
                </div>

                {/* Diferencia del arqueo: la misma cifra que registra el cierre */}
                <div className="flex justify-between items-center pt-2">
                  <p className="text-sm font-medium">{t('cashbox.difference')}</p>
                  <div className="flex items-center gap-2">
                    <DifferenceAmount session={session} className="font-bold text-lg" />
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
            </Link>
          ))}
        </div>

        {/* Desktop: Table */}
        <Card className="hidden lg:block">
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-4 font-medium">{t('cashbox.openingUser')}</th>
                  <th className="text-left p-4 font-medium">{t('cashbox.closingUser')}</th>
                  <th className="text-left p-4 font-medium">{t('cashbox.openingDate')}</th>
                  <th className="text-left p-4 font-medium">{t('cashbox.closingDate')}</th>
                  <th className="text-right p-4 font-medium">{t('cashbox.openingAmount')}</th>
                  <th className="text-right p-4 font-medium">{t('cashbox.closingAmount')}</th>
                  <th className="text-right p-4 font-medium">{t('cashbox.difference')}</th>
                  <th className="text-center p-4 font-medium">{t('cashbox.closedColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session: CashboxSession) => (
                  <tr
                    key={session.id}
                    className="border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    onClick={() => navigate(`/cashbox/history/${session.id}`)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/cashbox/history/${session.id}`)
                    }}
                  >
                    <td className="p-4 font-medium">{session.opened_by_name || '-'}</td>
                    <td className="p-4">{session.closed_by_name || '-'}</td>
                    <td className="p-4 text-sm">
                      {session.opening_time ? formatDateTime(session.opening_time) : '-'}
                    </td>
                    <td className="p-4 text-sm">
                      {session.closing_time ? formatDateTime(session.closing_time) : '-'}
                    </td>
                    <td className="p-4 text-right">
                      {formatCurrency(session.initial_amount)}
                    </td>
                    <td className="p-4 text-right">
                      {session.final_amount !== null ? formatCurrency(session.final_amount) : '-'}
                    </td>
                    <td className="p-4 text-right font-bold">
                      <DifferenceAmount session={session} />
                    </td>
                    <td className="p-4 text-center">
                      {session.is_open ? (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                          <X className="h-3 w-3 mr-1" />
                          {t('common.no')}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <Check className="h-3 w-3 mr-1" />
                          {t('common.yes')}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <p className="text-sm text-muted-foreground">
              {t('common.page')} {page} {t('common.of')} {totalPages}
            </p>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </>
    )}
    </div>
  )
}
